import React from 'react';
import { ClubLandRoleToggle } from './ClubLandRoleToggle';
import { clubLandHasEditableShape } from '../utils/clubAreaBoard';
import { clubLandSyncDefaultName } from '../utils/clubBoundaryRole';
import { Form } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { YButton } from '../../../ui/YButton';
import { YSheet } from '../../../ui/sheet/YSheet';

export function CreateFeatureModal({ 
  show, 
  onHide, 
  draftFeature,
  draftName,
  setDraftName,
  draftNotes,
  setDraftNotes,
  draftIcon,
  setDraftIcon,
  draftRadius,
  setDraftRadius,
  drawRef,
  onSave,
  onCancel,
  boundaryMode,
  clubId,
  isClubBoundary,
  setIsClubBoundary,
  // Club-specific fields
  isClubMap = false,
  draftFeatureType,
  setDraftFeatureType,
  draftOptimalWinds,
  setDraftOptimalWinds,
  draftIsStand,
  setDraftIsStand,
  // Polygon styling fields
  draftLineColor,
  setDraftLineColor,
  draftFillColor,
  setDraftFillColor,
  draftFillOpacity,
  setDraftFillOpacity
}) {
  
  const createTitle = boundaryMode === 'define'
    ? 'Define Club Boundary'
    : isClubMap
      ? (draftFeature?.type === 'polygon' ? 'Create club boundary' : 'Create Club Feature')
      : 'Create Feature';

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker="SAVE"
      title={createTitle}
      testId="create-feature-sheet"
      footer={
        <>
          <YButton type="button" variant="secondary" onClick={onCancel}>Cancel</YButton>
          <YButton type="button" onClick={onSave}>Save</YButton>
        </>
      }
    >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Enter feature name"
            />
          </Form.Group>
          
          {/* Feature type (club only for now) */}
          {isClubMap && draftFeature?.type === 'point' && (
            <Form.Group className="mb-3">
              <Form.Label>Feature Type</Form.Label>
              <Form.Select
                value={draftFeatureType}
                onChange={(e) => setDraftFeatureType(e.target.value)}
              >
                <option value="stand">Tree Stand</option>
                <option value="blind">Ground Blind</option>
                <option value="feeder">Feeder</option>
                <option value="water">Water Source</option>
                <option value="trail">Trail</option>
                <option value="road">Road</option>
                <option value="food_plot">Food Plot</option>
                <option value="bedding">Bedding Area</option>
                <option value="camp">Camp</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
          )}

          {/* Stand checkbox (personal map only): when checked, Optimal Winds section is shown */}
          {!isClubMap && draftFeature?.type === 'point' && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="draft-is-stand"
                label="Stand"
                checked={draftIsStand ?? false}
                onChange={(e) => {
                  setDraftIsStand(e.target.checked);
                  if (!e.target.checked) setDraftOptimalWinds([]);
                }}
              />
            </Form.Group>
          )}
          
          {/* Optimal winds: club always; personal only when Stand is checked */}
          {draftFeature?.type === 'point' && (isClubMap || draftIsStand) && (
            <Form.Group className="mb-3">
              <Form.Label>Optimal Winds</Form.Label>
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {['N','NE','E','SE','S','SW','W','NW'].map(dir => (
                  <Form.Check
                    key={dir}
                    inline
                    type="checkbox"
                    id={`draft-wind-${dir}`}
                    label={dir}
                    checked={(Array.isArray(draftOptimalWinds) ? draftOptimalWinds : []).includes(dir)}
                    onChange={(e) => {
                      const prev = Array.isArray(draftOptimalWinds) ? draftOptimalWinds : [];
                      if (e.target.checked) {
                        setDraftOptimalWinds(Array.from(new Set([...prev, dir])));
                      } else {
                        setDraftOptimalWinds(prev.filter(d => d !== dir));
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>
          )}
          
          {/* Polygon styling (when creating polygons) */}
          {draftFeature?.type === 'polygon' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Line Color</Form.Label>
                <Form.Control type="color" value={draftLineColor || '#f6f5f2'} onChange={(e) => setDraftLineColor && setDraftLineColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Color</Form.Label>
                <Form.Control type="color" value={draftFillColor || '#f6f5f2'} onChange={(e) => setDraftFillColor && setDraftFillColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Opacity</Form.Label>
                <Form.Range min={0} max={1} step={0.05} value={typeof draftFillOpacity === 'number' ? draftFillOpacity : 0} onChange={(e) => setDraftFillOpacity && setDraftFillOpacity(parseFloat(e.target.value))} />
              </Form.Group>
            </>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              placeholder="Optional notes" 
            />
          </Form.Group>
          
          {draftFeature?.type === 'point' && (
            <Form.Group className="mb-3">
              <Form.Label>Radius (yards)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="1000"
                value={draftRadius}
                onChange={(e) => setDraftRadius(parseInt(e.target.value) || 0)}
                placeholder="0 (no circle)"
              />
              <Form.Text className="text-muted">
                Add a circle around this point (0 = no circle)
              </Form.Text>
            </Form.Group>
          )}
          
          {/* Polygon styling (club boundary define mode) */}
          {boundaryMode === 'define' && draftFeature?.type === 'polygon' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Line Color</Form.Label>
                <Form.Control type="color" value={draftLineColor || '#f6f5f2'} onChange={(e) => setDraftLineColor && setDraftLineColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Color</Form.Label>
                <Form.Control type="color" value={draftFillColor || '#f6f5f2'} onChange={(e) => setDraftFillColor && setDraftFillColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Opacity</Form.Label>
                <Form.Range min={0} max={1} step={0.05} value={typeof draftFillOpacity === 'number' ? draftFillOpacity : 0} onChange={(e) => setDraftFillOpacity && setDraftFillOpacity(parseFloat(e.target.value))} />
              </Form.Group>
            </>
          )}

          {(isClubMap || boundaryMode === 'define') && draftFeature?.type === 'polygon' && setIsClubBoundary && (
            <Form.Group className="mb-3">
              <Form.Label>Land type</Form.Label>
              <ClubLandRoleToggle
                isBoundary={isClubBoundary !== false}
                onChange={(nextIsBoundary) => {
                  setIsClubBoundary(nextIsBoundary);
                  setDraftName(clubLandSyncDefaultName(draftName, nextIsBoundary));
                }}
              />
            </Form.Group>
          )}
        </Form>
    </YSheet>
  );
}

export function EditFeatureModal({ 
  show, 
  onHide, 
  editingFeature,
  editName,
  setEditName,
  editNotes,
  setEditNotes,
  editIcon,
  setEditIcon,
  editRadius,
  setEditRadius,
  drawRef,
  onSave,
  onCancel,
  onDelete,
  onStartGeometryEdit,
  // Optional editable fields for club hunt locations
  editFeatureType,
  setEditFeatureType,
  editOptimalWind,
  setEditOptimalWind,
  editOptimalWinds,
  setEditOptimalWinds,
  editIsStand,
  setEditIsStand,
  // Polygon styling
  editLineColor,
  setEditLineColor,
  editFillColor,
  setEditFillColor,
  editFillOpacity,
  setEditFillOpacity,
  editIsBoundary,
  setEditIsBoundary,
  // Feeding config (optional for point features)
  editFeedingMode,
  setEditFeedingMode,
  editFeedCapacityLbs,
  setEditFeedCapacityLbs,
  editConsumptionRateCode,
  setEditConsumptionRateCode,
  editRefillThresholdPct,
  setEditRefillThresholdPct
}) {
  
  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker="EDIT"
      title="Edit Feature"
      testId="edit-feature-dialog"
      footerStart={(
        <>
          {typeof onStartGeometryEdit === 'function' && clubLandHasEditableShape(editingFeature) ? (
            <YButton
              type="button"
              variant="secondary"
              data-testid="edit-feature-shape"
              onClick={onStartGeometryEdit}
            >
              Edit shape
            </YButton>
          ) : null}
          {onDelete ? (
            <YButton
              type="button"
              variant="danger"
              data-testid="edit-feature-delete"
              onClick={onDelete}
            >
              <FaTrash aria-hidden="true" /> Delete
            </YButton>
          ) : null}
        </>
      )}
      footer={
        <>
          <YButton type="button" variant="secondary" onClick={onCancel}>Cancel</YButton>
          <YButton type="button" onClick={onSave}>Save</YButton>
        </>
      }
    >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter feature name"
            />
          </Form.Group>
          {(editingFeature?.featureType === 'hunt_location' || editingFeature?.geojson?.type === 'Point') && (
            <>
              {setEditFeatureType ? (
                <Form.Group className="mb-3">
                  <Form.Label>Feature Type</Form.Label>
                  <Form.Select
                    value={editFeatureType}
                    onChange={(e) => setEditFeatureType(e.target.value)}
                  >
                    <option value="stand">Tree Stand</option>
                    <option value="blind">Ground Blind</option>
                    <option value="feeder">Feeder</option>
                    <option value="water">Water Source</option>
                  </Form.Select>
                </Form.Group>
              ) : null}

              {/* Stand checkbox (personal point only): when checked, Optimal Winds shown */}
              {editingFeature?.geojson?.type === 'Point' && editingFeature?.featureType !== 'hunt_location' && setEditIsStand != null && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="edit-is-stand"
                    label="Stand"
                    checked={editIsStand ?? false}
                    onChange={(e) => {
                      setEditIsStand(e.target.checked);
                      if (!e.target.checked && setEditOptimalWinds) setEditOptimalWinds([]);
                    }}
                  />
                </Form.Group>
              )}

              {/* Multi-select Optimal Winds: club always; personal only when Stand checked */}
              {setEditOptimalWinds && (editingFeature?.featureType === 'hunt_location' || (editingFeature?.geojson?.type === 'Point' && editIsStand)) ? (
                <Form.Group className="mb-3">
                  <Form.Label>Optimal Winds</Form.Label>
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    {['N','NE','E','SE','S','SW','W','NW'].map(dir => {
                      const fromArray = Array.isArray(editOptimalWinds) ? editOptimalWinds : [];
                      const seeded = (!fromArray.length && editOptimalWind) ? [editOptimalWind] : fromArray;
                      const checked = seeded.includes(dir);
                      return (
                        <Form.Check
                          key={dir}
                          inline
                          type="checkbox"
                          id={`edit-wind-${dir}`}
                          label={dir}
                          checked={checked}
                          onChange={(e) => {
                            const prev = seeded;
                            if (e.target.checked) {
                              setEditOptimalWinds(Array.from(new Set([...prev, dir])));
                            } else {
                              setEditOptimalWinds(prev.filter(d => d !== dir));
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </Form.Group>
              ) : null}
            </>
          )}

          {/* Polygon styling for edit */}
          {(editingFeature?.geojson?.type === 'Polygon' || editingFeature?.geojson?.type === 'MultiPolygon' || editingFeature?.type === 'polygon') && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Line Color</Form.Label>
                <Form.Control type="color" value={editLineColor || '#f6f5f2'} onChange={(e) => setEditLineColor && setEditLineColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Color</Form.Label>
                <Form.Control type="color" value={editFillColor || '#f6f5f2'} onChange={(e) => setEditFillColor && setEditFillColor(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fill Opacity</Form.Label>
                <Form.Range min={0} max={1} step={0.05} value={typeof editFillOpacity === 'number' ? editFillOpacity : 0} onChange={(e) => setEditFillOpacity && setEditFillOpacity(parseFloat(e.target.value))} />
              </Form.Group>
              {editingFeature?.featureType === 'hunt_boundary' && setEditIsBoundary && (
                <Form.Group className="mb-3">
                  <Form.Label>Land type</Form.Label>
                  <ClubLandRoleToggle
                    isBoundary={editIsBoundary !== false}
                    onChange={setEditIsBoundary}
                  />
                </Form.Group>
              )}
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </Form.Group>

          {(editingFeature?.featureType === 'hunt_location' || editingFeature?.geojson?.type === 'Point') && (
            <Form.Group className="mb-3">
              <Form.Label>Radius (yards)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="1000"
                value={editRadius}
                onChange={(e) => setEditRadius(parseInt(e.target.value) || 0)}
                placeholder="0 (no circle)"
              />
            </Form.Group>
          )}

        {(editingFeature?.featureType === 'hunt_location' || editingFeature?.geojson?.type === 'Point') && (
          <>
            <hr />
            <h6>Feeding</h6>
            <Form.Group className="mb-3">
              <Form.Label>Mode</Form.Label>
              <Form.Select value={editFeedingMode || ''} onChange={(e) => setEditFeedingMode && setEditFeedingMode(e.target.value || null)}>
                <option value="">Disabled</option>
                <option value="hopper">Hopper (capacity)</option>
                <option value="ground">Ground (no capacity)</option>
              </Form.Select>
            </Form.Group>
            {editFeedingMode === 'hopper' && (
              <Form.Group className="mb-3">
                <Form.Label>Capacity (lbs)</Form.Label>
                <Form.Control type="number" min="1" value={editFeedCapacityLbs || ''} onChange={(e) => setEditFeedCapacityLbs && setEditFeedCapacityLbs(parseInt(e.target.value) || null)} />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Consumption rate</Form.Label>
              <Form.Select value={editConsumptionRateCode || ''} onChange={(e) => setEditConsumptionRateCode && setEditConsumptionRateCode(e.target.value || null)}>
                <option value="">Not set</option>
                <option value="low">Low (≈5 lbs/day)</option>
                <option value="avg">Average (≈10 lbs/day)</option>
                <option value="high">High (≈20 lbs/day)</option>
                <option value="very_high">Very high (≈30 lbs/day)</option>
              </Form.Select>
            </Form.Group>
            {editFeedingMode === 'hopper' && (
              <Form.Group className="mb-3">
                <Form.Label>Refill threshold (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={typeof editRefillThresholdPct === 'number' ? editRefillThresholdPct : 20}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    if (setEditRefillThresholdPct) {
                      setEditRefillThresholdPct(Number.isFinite(next) ? Math.max(0, Math.min(100, next)) : 20);
                    }
                  }}
                />
              </Form.Group>
            )}
          </>
        )}
        </Form>
    </YSheet>
  );
}
